'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const react = require('@emotion/react');
const styleEncapsulation = require('@zapier/style-encapsulation');
const React = require('react');
const theme_Animation = require('../../../theme/Animation.cjs.js');
const theme_Colors = require('../../../theme/Colors.cjs.js');
const components_navigation_Button_BaseButton = require('../../navigation/Button/BaseButton.cjs.js');
const components_display_Icon_Icon = require('../Icon/Icon.cjs.js');
const components_navigation_Indicator_Indicator = require('../../navigation/Indicator/Indicator.cjs.js');
const components_navigation_Paginator_Paginator = require('../../navigation/Paginator/Paginator.cjs.js');
const components_display_Carousel_ScrollSnap_ScrollSnap = require('./ScrollSnap/ScrollSnap.cjs.js');
const utils_uniqueId = require('../../../utils/uniqueId.cjs.js');
const styleMixins_reset = require('../../../style-mixins/reset.cjs.js');

const _interopDefault = e => e && e.__esModule ? e : { default: e };

const React__default = /*#__PURE__*/_interopDefault(React);

const Styles = styleEncapsulation.labelStyles("Carousel", {
  carousel: (props, { isFirstPage, isLastPage }) => [
    react.css(Object.assign(Object.assign({}, styleMixins_reset.boxSizing), { position: "relative" })),
    (props.edgeColor || props.showArrows) && styleEncapsulation.withModifier("fadeEdges", {
      "&::before, &::after": {
        content: '""',
        position: "absolute",
        zIndex: 1,
        top: 0,
        bottom: 0,
        width: 100,
        pointerEvents: "none",
        transition: theme_Animation.Animation.transitionValue
      },
      "&::before": {
        opacity: isFirstPage ? 0 : 1,
        left: 0,
        right: "auto",
        background: `linear-gradient(to right, ${props.edgeColor || theme_Colors.Colors.PrimeWhite}, #fff0)`
      },
      "&::after": {
        opacity: isLastPage ? 0 : 1,
        left: "auto",
        right: 0,
        background: `linear-gradient(to left, ${props.edgeColor || theme_Colors.Colors.PrimeWhite}, #fff0)`
      }
    })
  ],
  paginator: () => [
    react.css(Object.assign(Object.assign({}, styleMixins_reset.boxSizing), { marginTop: 30, gridTemplateRows: "auto auto", gridTemplateColumns: "100%", justifyItems: "center", display: "grid" }))
  ],
  arrowButton: (props) => [
    react.css(Object.assign(Object.assign({}, styleMixins_reset.boxSizing), { transition: theme_Animation.Animation.transitionValue, position: "absolute", zIndex: 3, top: props.yOffset, transform: "translateY(-50%)", display: "none", "&:hover > button": {
      boxShadow: "none !important"
    }, "@media (min-width: 1230px)": {
      display: "block"
    } })),
    styleEncapsulation.withModifier(props.direction, {
      prev: {
        left: "20px"
      },
      next: {
        right: "20px"
      }
    }[props.direction]),
    props.isDisabled && styleEncapsulation.withModifier("disabled", {
      pointerEvents: "none",
      "& > button": {
        color: `${theme_Colors.Colors.PrimeWhite} !important`,
        border: 0,
        backgroundColor: `${theme_Colors.Colors.GrayWarm5} !important`
      }
    })
  ],
  scrollSnapChild: (fadeInactive, isActiveSlide) => [
    react.css(Object.assign(Object.assign({}, styleMixins_reset.boxSizing), { transition: "opacity 0.5s", height: "100%" })),
    fadeInactive && !isActiveSlide && styleEncapsulation.withModifier("faded", {
      opacity: 0.3
    })
  ]
});
const ArrowButton = (props) => {
  const icon = {
    prev: "arrowSmallLeft",
    next: "arrowSmallRight"
  }[props.direction];
  return react.jsx(
    "span",
    { css: Styles.arrowButton(props), "data-zds": true },
    react.jsx(
      components_navigation_Button_BaseButton.BaseButton,
      { "aria-label": `${props.direction} image`, color: props.color, disabled: props.isDisabled, isPill: true, isSquare: true, onClick: props.onClick, size: "large" },
      react.jsx(components_display_Icon_Icon.Icon, { isBlock: true, name: icon, size: 30 })
    )
  );
};
const Carousel = ({ children, slideWidth, arrowOffset = "50%", edgeColor = void 0, fadeInactive = void 0, gap: _deprecatedGapProp = 20, id = void 0, onActiveSlideChange = void 0, paginatorStyle = void 0, showArrows = void 0, showPaginator = false, arrowButtonColor = "primary" }) => {
  const gap = 20;
  const [pageIndex, setPageIndex] = React.useState(0);
  const isFirstPage = pageIndex === 0;
  const isLastPage = pageIndex === React.Children.count(children) - 1;
  const changePageIndex = (nextIndex, source) => {
    setPageIndex(nextIndex);
    if (onActiveSlideChange && pageIndex !== nextIndex) {
      onActiveSlideChange(nextIndex, pageIndex, { source });
    }
  };
  const paginatorAriaLabel = id ? `${id} carousel paginator` : utils_uniqueId.uniqueId("carousel paginator ");
  let paginator = paginatorStyle && {
    paginator: react.jsx(components_navigation_Paginator_Paginator.Paginator, { "aria-label": paginatorAriaLabel, currentPageIndex: pageIndex, onPaginate: ({ pageIndex: index }) => changePageIndex(index, "paginator"), pageUrls: Array.from({ length: React.Children.count(children) }) }),
    indicator: react.jsx(components_navigation_Indicator_Indicator.Indicator, { "aria-label": "List of images", color: "gray", count: React.Children.count(children), noun: "image", onChangeIndex: (index) => changePageIndex(index, "indicator"), selectedIndex: pageIndex })
  }[paginatorStyle];
  if (showPaginator || paginatorStyle) {
    paginator = react.jsx(components_navigation_Paginator_Paginator.Paginator, { "aria-label": paginatorAriaLabel, currentPageIndex: pageIndex, onPaginate: ({ pageIndex: index }) => changePageIndex(index, "paginator"), pageUrls: Array.from({ length: React.Children.count(children) }) });
  }
  return react.jsx(
    "div",
    { id, "data-zds": true, css: Object.assign({}, styleMixins_reset.boxSizing) },
    react.jsx(
      "div",
      { css: Styles.carousel({ edgeColor, showArrows }, { isFirstPage, isLastPage }), "data-zds": true },
      showArrows && react.jsx(
        React.Fragment,
        null,
        react.jsx(ArrowButton, { direction: "prev", isDisabled: isFirstPage, yOffset: arrowOffset, color: arrowButtonColor, onClick: () => {
          changePageIndex(pageIndex - 1, "previous");
        } }),
        react.jsx(ArrowButton, { direction: "next", isDisabled: isLastPage, yOffset: arrowOffset, color: arrowButtonColor, onClick: () => {
          changePageIndex(pageIndex + 1, "next");
        } })
      ),
      react.jsx(components_display_Carousel_ScrollSnap_ScrollSnap.ScrollSnap, { gap, itemWidth: slideWidth, onScroll: (index) => {
        changePageIndex(index, "scroll");
      }, pageIndex, scrollSnapAlign: "center" }, React__default.default.Children.map(children, (child, i) => react.jsx("div", { css: Styles.scrollSnapChild(fadeInactive, pageIndex === i), key: i, "data-zds": true }, child)))
    ),
    paginator && react.jsx("div", { css: Styles.paginator(), "data-zds": true }, paginator)
  );
};

exports.Carousel = Carousel;
//# sourceMappingURL=Carousel.cjs.js.map
